<template>
  <div class="page-support">
    <landing-header />
    <main>

      <!-- HERO -->
      <section class="hero-support">
        <div class="max-w-6xl mx-auto px-4 text-center">
          <span class="badge-support">Support &amp; Accompagnement</span>
          <h1 class="hero-title">
            Vous n'êtes pas seul.<br/>
            <span class="gradient-text-support">On est là à chaque étape.</span>
          </h1>
          <p class="hero-sub">
            Termoflow ce n'est pas juste un logiciel qu'on vous livre et on disparaît. Notre mission est de vous accompagner vraiment — à l'onboarding, en cours d'utilisation, et quand vous en avez besoin.
          </p>
          <div class="hero-ctas">
            <a href="/contact"><button class="btn-primary-support">Parler à l'équipe</button></a>
            <a href="https://docs.termoflow.fr" target="_blank" class="btn-outline-large">Accéder à la documentation</a>
          </div>
          <!-- Promesse -->
          <div class="hero-promise">
            <div class="promise-item">
              <div class="promise-icon"><i class="fa-solid fa-clock"></i></div>
              <div class="promise-val">&lt; 4h</div>
              <div class="promise-lab">Temps de réponse moyen</div>
            </div>
            <div class="promise-sep"></div>
            <div class="promise-item">
              <div class="promise-icon"><i class="fa-solid fa-star"></i></div>
              <div class="promise-val">4,9/5</div>
              <div class="promise-lab">Satisfaction support</div>
            </div>
            <div class="promise-sep"></div>
            <div class="promise-item">
              <div class="promise-icon"><i class="fa-solid fa-user-tie"></i></div>
              <div class="promise-val">Humain</div>
              <div class="promise-lab">Pas de bot, jamais</div>
            </div>
            <div class="promise-sep"></div>
            <div class="promise-item">
              <div class="promise-icon"><i class="fa-solid fa-wrench"></i></div>
              <div class="promise-val">Métier CVC</div>
              <div class="promise-lab">On connaît votre secteur</div>
            </div>
          </div>
        </div>
      </section>

      <!-- MANIFESTE -->
      <section class="manifeste-section">
        <div class="max-w-4xl mx-auto px-4 text-center">
          <div class="manifeste-quote">
            <i class="fa-solid fa-quote-left"></i>
            <p>Notre vision est simple : vous fournir un logiciel qui fonctionne, et être disponibles dès que vous en avez besoin. Pas un chatbot, pas un ticket qui part dans le vide — une vraie réponse, d'une vraie personne, qui comprend votre métier.</p>
            <div class="manifeste-sign">
              <div class="ms-avatar">TF</div>
              <div>
                <div class="ms-name">L'équipe Termoflow</div>
                <div class="ms-role">Support &amp; Product</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- CANAUX DE SUPPORT -->
      <section class="py-24 bg-white">
        <div class="max-w-6xl mx-auto px-4">
          <div class="section-label-support text-center">Comment on vous aide</div>
          <h2 class="section-title text-center">Plusieurs façons de nous contacter,<br/>toutes avec une vraie réponse</h2>
          <p class="section-sub text-center mx-auto mb-16">Choisissez le canal qui vous convient — on s'adapte à votre façon de travailler, pas l'inverse.</p>
          <div class="support-channels">
            <div class="sc-card featured-sc">
              <div class="sc-icon"><i class="fa-solid fa-comment-dots"></i></div>
              <div class="sc-badge sc-badge-main">Prioritaire</div>
              <h3>Chat intégré</h3>
              <p>Posez votre question directement depuis Termoflow. Notre équipe répond en moins de 4 heures ouvrées — souvent bien avant.</p>
              <div class="sc-detail"><i class="fa-solid fa-clock"></i> Lun–Ven · 8h–17h</div>
              <div class="sc-detail"><i class="fa-solid fa-bolt"></i> Réponse &lt; 4h en moyenne</div>
            </div>
            <div class="sc-card">
              <div class="sc-icon sc-icon-email"><i class="fa-solid fa-envelope"></i></div>
              <h3>Email</h3>
              <p>Pour les questions détaillées, les demandes de fonctionnalité ou les remontées de bug. On traite chaque email sérieusement.</p>
              <div class="sc-detail"><i class="fa-solid fa-at"></i> contact@termoflow.fr</div>
              <div class="sc-detail"><i class="fa-solid fa-clock"></i> Réponse sous 24h ouvrées</div>
            </div>
            <div class="sc-card">
              <div class="sc-icon sc-icon-phone"><i class="fa-solid fa-phone"></i></div>
              <h3>Téléphone</h3>
              <p>Pour les urgences ou quand vous préférez parler à quelqu'un directement. Vous tombez sur une vraie personne de l'équipe.</p>
              <div class="sc-detail"><i class="fa-solid fa-phone"></i> Disponible sur demande</div>
              <div class="sc-detail"><i class="fa-solid fa-user"></i> Équipe technique directe</div>
            </div>
            <div class="sc-card">
              <div class="sc-icon sc-icon-docs"><i class="fa-solid fa-book-open"></i></div>
              <h3>Documentation</h3>
              <p>Une base de connaissances complète avec tutoriels vidéo, guides pas-à-pas et FAQ — pour trouver la réponse vous-même en 2 minutes.</p>
              <div class="sc-detail"><i class="fa-solid fa-video"></i> Tutoriels inclus</div>
            </div>
          </div>
        </div>
      </section>

      <!-- ONBOARDING -->
      <section class="py-24 bg-gray-50 border-y border-gray-100">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex flex-col lg:flex-row gap-16 items-center">
            <div class="lg:w-1/2">
              <div class="section-label-support">Onboarding</div>
              <h2 class="section-title">On configure Termoflow<br/>avec vous</h2>
              <p class="section-sub mb-10">À votre souscription, nous pouvons planifier une session d'onboarding en visio avec votre équipe. On paramètre ensemble vos types d'appareils, vos interventions, vos techniciens — vous n'avez rien à deviner.</p>
              <div class="onboarding-steps">
                <div class="ob-step">
                  <div class="ob-num">1</div>
                  <div>
                    <h4>Session de prise en main (1h)</h4>
                    <p>Visio avec un membre de l'équipe Termoflow. On configure ensemble les bases : appareils, techniciens, paramètres de facturation.</p>
                  </div>
                </div>
                <div class="ob-step">
                  <div class="ob-num">2</div>
                  <div>
                    <h4>Import de vos données existantes</h4>
                    <p>Vous avez des clients, des appareils, des contrats existants ? On vous aide à les importer depuis votre ancien outil ou un fichier Excel.</p>
                  </div>
                </div>
                <div class="ob-step">
                  <div class="ob-num">3</div>
                  <div>
                    <h4>Suivi</h4>
                    <p>Après le lancement, nous pouvons faire un point avec vous — questions, ajustements, nouvelles fonctionnalités à configurer.</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="lg:w-1/2">
              <div class="onboarding-timeline">
                <div class="ot-head">Votre parcours d'intégration</div>
                <div class="ot-steps">
                  <div class="ot-step">
                    <div class="ot-step-left">
                      <div class="ot-dot active-dot"></div>
                      <div class="ot-line"></div>
                    </div>
                    <div class="ot-content">
                      <div class="ot-tag">Jour 1</div>
                      <div class="ot-title">Accès + session d'onboarding</div>
                      <div class="ot-desc">Votre compte est créé, on se retrouve en visio pour tout configurer ensemble.</div>
                    </div>
                  </div>
                  <div class="ot-step">
                    <div class="ot-step-left">
                      <div class="ot-dot"></div>
                      <div class="ot-line"></div>
                    </div>
                    <div class="ot-content">
                      <div class="ot-tag">Semaine 1</div>
                      <div class="ot-title">Import de vos données</div>
                      <div class="ot-desc">Clients, appareils, historique — on vous aide à migrer proprement depuis votre ancien outil.</div>
                    </div>
                  </div>
                  <div class="ot-step">
                    <div class="ot-step-left">
                      <div class="ot-dot"></div>
                      <div class="ot-line"></div>
                    </div>
                    <div class="ot-content">
                      <div class="ot-tag">Semaine 2</div>
                      <div class="ot-title">Formation techniciens</div>
                      <div class="ot-desc">Tutoriels vidéo pour vos techniciens, prise en main de l'application mobile.</div>
                    </div>
                  </div>
                  <div class="ot-step">
                    <div class="ot-step-left">
                      <div class="ot-dot"></div>
                    </div>
                    <div class="ot-content">
                      <div class="ot-tag">J+30</div>
                      <div class="ot-title">Point de suivi</div>
                      <div class="ot-desc">On fait un bilan, on répond à vos questions et on ajuste la configuration si besoin.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ACCOMPAGNEMENT CONTINU -->
      <section class="py-24 bg-white">
        <div class="max-w-6xl mx-auto px-4">
          <div class="section-label-support text-center">Accompagnement continu</div>
          <h2 class="section-title text-center">On ne disparaît pas après la signature.</h2>
          <p class="section-sub text-center mx-auto mb-16">Notre engagement envers vous ne s'arrête pas à l'onboarding. Termoflow évolue en permanence — et on vous tient informé.</p>
          <div class="accomp-grid">
            <div class="accomp-card">
              <div class="ac-icon"><i class="fa-solid fa-newspaper"></i></div>
              <h3>Notes de mise à jour</h3>
              <p>À chaque mise à jour, vous recevez les notes de version détaillées avec des présentations pour les nouvelles fonctionnalités.</p>
            </div>
            <div class="accomp-card">
              <div class="ac-icon"><i class="fa-solid fa-lightbulb"></i></div>
              <h3>Conseils personnalisés</h3>
              <p>Lors de vos échanges avec notre équipe, on va au-delà du simple support technique — on vous suggère comment tirer encore plus de valeur de Termoflow.</p>
            </div>
            <div class="accomp-card">
              <div class="ac-icon"><i class="fa-solid fa-message"></i></div>
              <h3>Remontées prises en compte</h3>
              <p>Vous signalez un bug ou une idée d'amélioration — on vous répond dans les plus brefs délais. Rien ne tombe dans le vide.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="py-24 bg-gray-50 border-t border-gray-100">
        <div class="max-w-3xl mx-auto px-4">
          <div class="section-label-support text-center">Questions fréquentes</div>
          <h2 class="section-title text-center">Ce que les clients nous demandent souvent</h2>
          <div class="faq-list">
            <div class="faq-item" v-for="faq in faqs" :key="faq.q" @click="faq.open = !faq.open">
              <div class="faq-q">
                {{ faq.q }}
                <i :class="faq.open ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" aria-hidden="true"></i>
              </div>
              <div class="faq-a" v-if="faq.open">{{ faq.a }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA DOUBLE -->
      <section class="py-24 bg-white">
        <div class="max-w-5xl mx-auto px-4">
          <div class="cta-split">
            <div class="cta-split-card primary-cta-card">
              <div class="csc-icon"><i class="fa-solid fa-comment-dots"></i></div>
              <h3>Vous avez une question ?</h3>
              <p>Notre équipe répond en moins de 4 heures ouvrées. Posez votre question, on s'occupe du reste.</p>
              <a href="/contact"><button class="btn-white">Nous contacter</button></a>
            </div>
            <div class="cta-split-card secondary-cta-card">
              <div class="csc-icon-light"><i class="fa-solid fa-play"></i></div>
              <h3>Vous voulez voir Termoflow en action ?</h3>
              <p>Demandez une démonstration personnalisée — on vous montre les fonctionnalités qui vous intéressent, pas un pitch générique.</p>
              <a href="/contact"><button class="btn-orange">Demander une démo</button></a>
            </div>
          </div>
        </div>
      </section>

      <landing-roi /><landing-avis />
    </main>
    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'
export default {
  name: 'SupportPage',
  components: { LandingHeader, LandingFooter, LandingAvis, 'landing-roi': LandingROI },
  data() {
    return {
      faqs: [
        { q: 'Combien de temps dure l\'onboarding ?', a: 'La session d\'onboarding initiale dure environ 1 heure. Elle suffit pour configurer les bases et que vous soyez opérationnel. Des sessions complémentaires peuvent être planifiées si vous en avez besoin.', open: false },
        { q: 'Est-ce que le support est inclus dans l\'abonnement ?', a: 'Oui, totalement. Le support par chat et email est inclus dans tous les abonnements, sans exception et sans limite de tickets. Vous payez votre abonnement, vous avez accès à l\'équipe.', open: false },
        { q: 'Que se passe-t-il si je rencontre un bug critique ?', a: 'Les bugs critiques (qui bloquent votre activité) sont traités en priorité absolue. Notre objectif est de vous répondre en moins de 2 heures et de déployer un correctif le jour même si possible.', open: false },
        { q: 'Pouvez-vous m\'aider à migrer depuis mon ancien logiciel ?', a: 'Oui. On vous aide à exporter vos données depuis votre ancien outil et à les importer dans Termoflow. Selon la complexité, ça peut être fait en quelques heures ou en quelques jours.', open: false },
        { q: 'Vous êtes disponibles le week-end ?', a: 'Notre support est disponible en semaine de 8h à 17h. Pour les urgences, vous pouvez envoyer un email — nous traitons les urgences réelles (panne bloquante) même le week-end.', open: false },
      ]
    }
  }
}
</script>

<style scoped>
.gradient-text-support{background:linear-gradient(to right,#f97316,#ef4444);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}
.hero-support{padding:10rem 1rem 5rem;background:radial-gradient(ellipse at top right,#fff7ed,transparent 50%),radial-gradient(ellipse at bottom left,#fef2f2,transparent 50%);}
.badge-support{display:inline-block;padding:.5rem 1rem;background:#fff7ed;color:#c2410c;font-weight:800;font-size:.75rem;border-radius:9999px;margin-bottom:1.5rem;text-transform:uppercase;border:1px solid #fed7aa;letter-spacing:.05em;}
.hero-title{font-size:clamp(2.5rem,5vw,4rem);font-weight:900;color:#111827;line-height:1.15;margin-bottom:1.25rem;}
.hero-sub{font-size:1.15rem;color:#6b7280;max-width:680px;margin:0 auto 2rem;line-height:1.7;}
.hero-ctas{display:flex;flex-wrap:wrap;gap:1rem;justify-content:center;margin-bottom:3rem;}
/* Hero promise */
.hero-promise{display:inline-flex;align-items:center;background:white;border:1px solid #e5e7eb;border-radius:2rem;padding:1rem 0;flex-wrap:wrap;justify-content:center;}
.promise-item{display:flex;flex-direction:column;align-items:center;gap:.25rem;padding:.5rem 2.5rem;}
.promise-icon{font-size:.9rem;color:#f97316;margin-bottom:.15rem;}
.promise-val{font-size:1.5rem;font-weight:900;color:#111827;}
.promise-lab{font-size:.68rem;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em;}
.promise-sep{width:1px;height:2.5rem;background:#e5e7eb;}
/* Manifeste */
.manifeste-section{padding:5rem 0;background:#1e293b;}
.manifeste-quote{background:#0f172a;border:1px solid #334155;border-radius:2rem;padding:3rem;position:relative;}
.manifeste-quote .fa-quote-left{font-size:2rem;color:#f97316;display:block;margin-bottom:1.5rem;}
.manifeste-quote p{font-size:1.15rem;font-weight:600;color:#f1f5f9;line-height:1.75;margin-bottom:2rem;font-style:italic;}
.manifeste-sign{display:flex;align-items:center;gap:1rem;}
.ms-avatar{width:3rem;height:3rem;border-radius:50%;background:linear-gradient(135deg,#f97316,#ef4444);color:white;font-size:.85rem;font-weight:900;display:flex;align-items:center;justify-content:center;}
.ms-name{font-size:.9rem;font-weight:800;color:#f1f5f9;}
.ms-role{font-size:.75rem;font-weight:600;color:#64748b;}
/* Sections commons */
.section-label-support{display:inline-block;font-size:.7rem;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#f97316;margin-bottom:1rem;}
.section-title{font-size:2.25rem;font-weight:900;color:#111827;line-height:1.2;margin-bottom:1rem;}
.section-sub{font-size:1.05rem;color:#6b7280;line-height:1.7;max-width:38rem;}
/* Support channels */
.support-channels{display:grid;grid-template-columns:repeat(4,1fr);gap:1.25rem;}
@media(max-width:900px){.support-channels{grid-template-columns:1fr 1fr;}}
@media(max-width:560px){.support-channels{grid-template-columns:1fr;}}
.sc-card{background:white;border:1px solid #e5e7eb;border-radius:1.5rem;padding:2rem;position:relative;}
.sc-card.featured-sc{border-color:#fed7aa;background:linear-gradient(135deg,#fff,#fff7ed);}
.sc-icon{width:3rem;height:3rem;border-radius:.85rem;background:#fff7ed;color:#f97316;display:flex;align-items:center;justify-content:center;font-size:1.25rem;margin-bottom:1.25rem;}
.sc-icon-email{background:#eff6ff;color:#2563eb;}
.sc-icon-phone{background:#f0fdf4;color:#16a34a;}
.sc-icon-docs{background:#f5f3ff;color:#7c3aed;}
.sc-badge{font-size:.65rem;font-weight:800;padding:.25rem .7rem;border-radius:9999px;position:absolute;top:1rem;right:1rem;}
.sc-badge-main{background:#f97316;color:white;}
.sc-card h3{font-size:1rem;font-weight:800;color:#111827;margin-bottom:.65rem;}
.sc-card p{font-size:.87rem;color:#6b7280;line-height:1.65;margin-bottom:1rem;}
.sc-detail{font-size:.78rem;font-weight:700;color:#94a3b8;display:flex;align-items:center;gap:.5rem;margin-bottom:.35rem;}
.sc-detail i{font-size:.7rem;}
.sc-link{display:inline-block;font-size:.8rem;font-weight:800;color:#7c3aed;text-decoration:none;margin-top:.5rem;}
/* Onboarding */
.onboarding-steps{display:flex;flex-direction:column;gap:1.5rem;}
.ob-step{display:flex;gap:1.25rem;}
.ob-num{width:2rem;height:2rem;border-radius:50%;background:#f97316;color:white;font-size:.82rem;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:.1rem;}
.ob-step h4{font-size:.95rem;font-weight:800;color:#111827;margin-bottom:.4rem;}
.ob-step p{font-size:.87rem;color:#6b7280;line-height:1.6;}
/* Timeline */
.onboarding-timeline{background:white;border-radius:1.5rem;border:1px solid #e5e7eb;overflow:hidden;}
.ot-head{background:#f8fafc;border-bottom:1px solid #e5e7eb;padding:1rem 1.5rem;font-size:.75rem;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:.05em;}
.ot-steps{padding:1.25rem 1.5rem;display:flex;flex-direction:column;}
.ot-step{display:flex;gap:1rem;min-height:80px;}
.ot-step-left{display:flex;flex-direction:column;align-items:center;}
.ot-dot{width:12px;height:12px;border-radius:50%;background:#e5e7eb;flex-shrink:0;margin-top:.35rem;}
.ot-dot.active-dot{background:#f97316;box-shadow:0 0 0 3px #fed7aa;}
.ot-line{width:2px;flex:1;background:#f1f5f9;margin:.25rem 0;}
.ot-content{flex:1;padding-bottom:1rem;}
.ot-tag{font-size:.65rem;font-weight:800;color:#f97316;text-transform:uppercase;letter-spacing:.05em;margin-bottom:.25rem;}
.ot-title{font-size:.88rem;font-weight:800;color:#1e293b;margin-bottom:.3rem;}
.ot-desc{font-size:.8rem;color:#64748b;line-height:1.6;}
/* Accompagnement grid */
.accomp-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.25rem;}
@media(max-width:900px){.accomp-grid{grid-template-columns:1fr 1fr;}}
@media(max-width:560px){.accomp-grid{grid-template-columns:1fr;}}
.accomp-card{background:white;border:1px solid #f1f5f9;border-radius:1.25rem;padding:1.75rem;transition:box-shadow .2s,transform .2s;}
.accomp-card:hover{box-shadow:0 8px 30px rgba(0,0,0,.07);transform:translateY(-2px);}
.ac-icon{width:2.75rem;height:2.75rem;border-radius:.75rem;background:#fff7ed;color:#f97316;display:flex;align-items:center;justify-content:center;font-size:1.1rem;margin-bottom:1.25rem;}
.accomp-card h3{font-size:1rem;font-weight:800;color:#111827;margin-bottom:.65rem;}
.accomp-card p{font-size:.87rem;color:#6b7280;line-height:1.65;}
/* FAQ */
.faq-list{display:flex;flex-direction:column;gap:0;margin-top:2.5rem;border-radius:1.25rem;overflow:hidden;border:1px solid #e5e7eb;}
.faq-item{border-bottom:1px solid #f1f5f9;cursor:pointer;}
.faq-item:last-child{border-bottom:none;}
.faq-q{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 1.5rem;font-size:.95rem;font-weight:800;color:#1e293b;gap:1rem;}
.faq-q i{color:#94a3b8;font-size:.8rem;flex-shrink:0;}
.faq-a{padding:.25rem 1.5rem 1.25rem;font-size:.88rem;color:#6b7280;line-height:1.7;}
/* CTA split */
.cta-split{display:grid;grid-template-columns:1fr 1fr;gap:1.5rem;}
@media(max-width:700px){.cta-split{grid-template-columns:1fr;}}
.cta-split-card{border-radius:1.5rem;padding:3rem;text-align:center;}
.primary-cta-card{background:#1e293b;color:white;}
.secondary-cta-card{background:#fff7ed;border:1px solid #fed7aa;}
.csc-icon{font-size:2rem;color:#f97316;margin-bottom:1.25rem;}
.csc-icon-light{font-size:2rem;color:#f97316;margin-bottom:1.25rem;}
.cta-split-card h3{font-size:1.25rem;font-weight:900;margin-bottom:.75rem;}
.primary-cta-card h3{color:white;}
.secondary-cta-card h3{color:#111827;}
.cta-split-card p{font-size:.9rem;line-height:1.65;margin-bottom:1.75rem;}
.primary-cta-card p{color:#94a3b8;}
.secondary-cta-card p{color:#6b7280;}
.btn-white{background:white;color:#1e293b;padding:1rem 2.5rem;border-radius:9999px;font-weight:800;border:none;font-size:.95rem;cursor:pointer;}
.btn-orange{background:#f97316;color:white;padding:1rem 2.5rem;border-radius:9999px;font-weight:800;border:none;font-size:.95rem;cursor:pointer;}
/* General buttons */
.btn-primary-support{background:linear-gradient(to right,#f97316,#ef4444);color:white;padding:1rem 2.5rem;border-radius:9999px;font-weight:800;border:none;cursor:pointer;font-size:.95rem;}
.btn-outline-large{border:2px solid #e5e7eb;padding:1rem 2.5rem;border-radius:9999px;font-weight:800;color:#1f2937;text-decoration:none;display:inline-block;}
@media(max-width:1024px){.hero-support{padding-top:8rem;}.section-title{font-size:1.75rem;}}
</style>